import axios from 'axios';

// Helper to read a cookie by name
const getCookie = (name) => {
  const matches = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
  );
  return matches ? decodeURIComponent(matches[1]) : undefined;
};

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true, // send cookies!
  headers: {
    'Content-Type': 'application/json',
  },
});

// List of public, unauthenticated routes that should NOT trigger a token refresh attempt on 401.
// These endpoints are expected to return 401/400 errors when data is invalid, not when the token is expired.
const PUBLIC_AUTH_ROUTES = [
  '/auth/set-initial-password', // The new endpoint for initial setup/reset
  '/auth/login',                // Login endpoint
];

// Request interceptor: add CSRF token (unchanged)
axiosInstance.interceptors.request.use(config => {
  const method = config.method && config.method.toLowerCase();
  const isModifying = ['post', 'put', 'patch', 'delete'].includes(method);

  if (isModifying) {
    const isRefresh = config.url?.includes('/auth/refresh');
    const csrfToken = getCookie(isRefresh ? 'csrf_refresh_token' : 'csrf_access_token');

    if (csrfToken) {
      config.headers['X-CSRF-TOKEN'] = csrfToken;
    }
  }

  return config;
}, error => Promise.reject(error));

// Response interceptor: auto-refresh access token on 401
axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    const isUnauthorized = error.response?.status === 401;
    const requestUrl = originalRequest.url;

    // Check if the request URL is one of the public authentication routes
    const isPublicAuthRoute = PUBLIC_AUTH_ROUTES.some(route => 
      requestUrl?.includes(route)
    );

    // CRITICAL LOGIC CHECK: 
    // Only attempt refresh if it's a 401, not a retry, not a refresh call itself, 
    // AND it's NOT a public auth endpoint.
    if (
      isUnauthorized &&
      !originalRequest._retry &&
      !requestUrl?.includes('/auth/refresh') &&
      !isPublicAuthRoute 
    ) {
      originalRequest._retry = true;

      try {
        // Attempt to refresh the access token
        await axiosInstance.post('/auth/refresh', {}, { withCredentials: true });
        // Re-send the original request with the new token
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // If refresh fails (refresh token expired/invalid), redirect to login
        console.error('Refresh token failed:', refreshError);
        window.location.href = '/login'; 
        return Promise.reject(refreshError);
      }
    }
    
    // For all other errors (including 401s on public routes), pass the error 
    // to the component's local catch block for specific handling.
    return Promise.reject(error);
  }
);

export default axiosInstance;